// React Aria's toast API is still marked UNSTABLE_. Keep every use of it in this
// file so a rename upstream is a one-file change.
import {
  Button as AriaButton,
  UNSTABLE_Toast as AriaToast,
  UNSTABLE_ToastContent as AriaToastContent,
  UNSTABLE_ToastQueue as AriaToastQueue,
  UNSTABLE_ToastRegion as AriaToastRegion,
  Text,
} from 'react-aria-components';

import styles from './Toast.module.css';

export interface ToastContent {
  /** The outcome in plain words, named after the action: "Payment received". */
  title: string;
  description?: string;
}

/** DESIGN.md §7: toasts auto-dismiss after 5 seconds and pause while hovered or focused. */
export const TOAST_TIMEOUT_MS = 5000;

export class ToastQueue {
  readonly #queue = new AriaToastQueue<ToastContent>({ maxVisibleToasts: 3 });

  /** @internal */
  get queue(): AriaToastQueue<ToastContent> {
    return this.#queue;
  }

  /**
   * Shows a confirmation. Never use a toast for an error that needs action; put those
   * inline where the person can fix them.
   */
  show(content: ToastContent): () => void {
    const key = this.#queue.add(content, { timeout: TOAST_TIMEOUT_MS });
    return () => {
      this.#queue.close(key);
    };
  }
}

export function ToastRegion({ queue }: { queue: ToastQueue }) {
  return (
    <AriaToastRegion queue={queue.queue} className={styles.region}>
      {({ toast }) => (
        <AriaToast toast={toast} className={styles.toast}>
          <AriaToastContent className={styles.content}>
            <Text slot="title" className={styles.title}>
              {toast.content.title}
            </Text>
            {toast.content.description === undefined ? null : (
              <Text slot="description" className={styles.description}>
                {toast.content.description}
              </Text>
            )}
          </AriaToastContent>
          <AriaButton slot="close" aria-label="Dismiss" className={styles.close}>
            Dismiss
          </AriaButton>
        </AriaToast>
      )}
    </AriaToastRegion>
  );
}
